
import React, { useState } from 'react';
import { useGetFarms, useDeleteFarm } from '@/hooks/use-farm';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Upload, Trash2, Edit, Eye } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from '@/hooks/use-toast';

const FarmList: React.FC = () => {
  const { data: farms, isLoading, error } = useGetFarms();
  const deleteFarm = useDeleteFarm();
  const navigate = useNavigate();
  const [farmToDelete, setFarmToDelete] = useState<string | null>(null);
  
  const handleDelete = async () => {
    if (!farmToDelete) return;
    
    try {
      await deleteFarm.mutateAsync(farmToDelete);
      toast({
        title: "Farm deleted",
        description: "The farm has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting farm:', error);
      toast({
        title: "Error",
        description: "Failed to delete farm. Please try again.",
        variant: "destructive"
      });
    } finally {
      setFarmToDelete(null);
    }
  };
  
  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-800 rounded-md">
        Error loading farms: {error.message}
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Farms</h1>
          <p className="text-muted-foreground">Manage your farms and crops</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/farms/upload')}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Farms
          </Button>
          <Button onClick={() => navigate('/farms/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Farm
          </Button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex flex-col space-y-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden flex flex-row items-center p-4 space-x-4">
              <div className="w-1/3">
                <Skeleton className="h-5 w-full mb-1" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <div className="w-2/3">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </Card>
          ))}
        </div>
      ) : farms && farms.length > 0 ? (
        <div className="flex flex-col divide-y">
          {farms.map((farm) => (
            <Card key={farm.id} className="overflow-hidden hover:shadow-md transition-shadow flex flex-row items-center p-4 justify-between">
              <div className="flex flex-col w-2/3">
                <CardTitle>{farm.farmer_name}'s Farm</CardTitle>
                <CardDescription className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" /> {farm.region}
                </CardDescription>
                <div className="flex gap-6 mt-2 text-sm text-muted-foreground">
                  <div>
                    <span className="font-semibold text-foreground">Size:</span> {farm.land_size} ha
                  </div>
                  {farm.irrigation_type && (
                    <div>
                      <span className="font-semibold text-foreground">Irrigation:</span> {farm.irrigation_type}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/farms/${farm.id}/edit`)}>
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setFarmToDelete(farm.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/farms/${farm.id}`)}>
                  <Eye className="h-4 w-4 mr-1" /> View
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center p-8 border border-dashed rounded-lg">
          <h3 className="font-medium text-lg mb-2">No farms found</h3>
          <p className="text-muted-foreground mb-4">Get started by adding your first farm</p>
          <div className="flex justify-center gap-4">
            <Button onClick={() => navigate('/farms/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Farm
            </Button>
            <Button variant="outline" onClick={() => navigate('/farms/upload')}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Farms
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!farmToDelete} onOpenChange={(open) => !open && setFarmToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this farm?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the farm
              and all associated data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleteFarm.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FarmList;

